import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { Save, CreditCard } from 'lucide-react';

const Profile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState({
    user_id: user.id,
    name: '',
    age_range: '',
    location: '',
    school_year: '',
    interests: [],
    preferred_trades: [],
    availability: '',
    has_paid: false,
    assessments_completed: false,
    consented: false,
    readiness_score: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_BASE}/apprentice/profile`, {
        params: { user_id: user.id }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/apprentice/profile`, profile);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePayment = async () => {
    try {
      await axios.post(`${API_BASE}/apprentice/payment`, { user_id: user.id });
      toast.success('Payment successful! Assessments unlocked.');
      setProfile({ ...profile, has_paid: true });
      setShowPayment(false);
    } catch (error) {
      toast.error('Payment failed');
    }
  };

  const toggleInterest = (interest) => {
    setProfile(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const toggleTrade = (trade) => {
    setProfile(prev => ({
      ...prev,
      preferred_trades: prev.preferred_trades.includes(trade)
        ? prev.preferred_trades.filter(t => t !== trade)
        : [...prev.preferred_trades, trade]
    }));
  };

  const allInterests = ['Technology', 'Building', 'Problem Solving', 'Teamwork', 'Design', 'Hands-on Work', 'Customer Service', 'Innovation'];
  const allTrades = ['IT Support', 'Construction', 'Electrical', 'Plumbing', 'Manufacturing', 'Automotive', 'Carpentry', 'Hospitality'];
  const ageRanges = ['16-18', '17-19', '18-20'];
  const schoolYears = ['First Year', 'Second Year', 'Third Year', 'Fourth Year'];
  const availabilityOptions = ['Full-time from May 2026', 'Full-time from June 2026', 'Full-time from July 2026', 'Part-time'];
  const locations = ['Dublin', 'Cork', 'Galway', 'Limerick', 'Waterford', 'Kilkenny'];

  if (loading) {
    return <div className="p-8 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D9488]"></div></div>;
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Profile</h1>
            <p className="text-lg text-slate-600">Complete your profile to unlock assessments</p>
          </div>
          {!profile.has_paid && profile.name && (
            <Button
              onClick={() => setShowPayment(true)}
              className="bg-[#0D9488] hover:bg-[#115E59] rounded-full"
              data-testid="open-payment-btn"
            >
              <CreditCard className="mr-2 w-4 h-4" />
              Pay €500
            </Button>
          )}
        </div>

        {showPayment && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPayment(false)}>
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Complete Payment</h3>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-600">Assessment Package</span>
                  <span className="text-2xl font-bold">€500</span>
                </div>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li className="flex items-center">✓ 3 Professional Assessments</li>
                  <li className="flex items-center">✓ AI CV Builder Access</li>
                  <li className="flex items-center">✓ Employer Matching</li>
                  <li className="flex items-center">✓ Direct Messaging</li>
                </ul>
              </div>
              <div className="space-y-4 mb-6">
                <div>
                  <Label>Card Number</Label>
                  <Input placeholder="4242 4242 4242 4242" className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Expiry</Label>
                    <Input placeholder="MM/YY" className="mt-1" />
                  </div>
                  <div>
                    <Label>CVC</Label>
                    <Input placeholder="123" className="mt-1" />
                  </div>
                </div>
              </div>
              <Button
                onClick={handlePayment}
                className="w-full bg-[#0D9488] hover:bg-[#115E59] rounded-full"
                data-testid="confirm-payment-btn"
              >
                Pay €500
              </Button>
              <p className="text-xs text-center text-slate-500 mt-4">This is a demo payment. No real transaction will occur.</p>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-2xl p-8 space-y-6">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              placeholder="John Murphy"
              className="mt-1"
              data-testid="profile-name-input"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="age_range">Age Range *</Label>
              <select
                id="age_range"
                value={profile.age_range}
                onChange={(e) => setProfile({ ...profile, age_range: e.target.value })}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/20 mt-1"
                data-testid="profile-age-select"
              >
                <option value="">Select age range</option>
                {ageRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="location">Location *</Label>
              <select
                id="location"
                value={profile.location}
                onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/20 mt-1"
                data-testid="profile-location-select"
              >
                <option value="">Select location</option>
                {locations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="school_year">School Year *</Label>
              <select
                id="school_year"
                value={profile.school_year}
                onChange={(e) => setProfile({ ...profile, school_year: e.target.value })}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/20 mt-1"
              >
                <option value="">Select year</option>
                {schoolYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="availability">Availability *</Label>
              <select
                id="availability"
                value={profile.availability}
                onChange={(e) => setProfile({ ...profile, availability: e.target.value })}
                className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/20 mt-1"
              >
                <option value="">Select availability</option>
                {availabilityOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label>Interests</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {allInterests.map(interest => (
                <button
                  key={interest}
                  onClick={() => toggleInterest(interest)}
                  className={`px-4 py-2 rounded-full border transition-all ${
                    profile.interests.includes(interest)
                      ? 'bg-[#0D9488] text-white border-[#0D9488]'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-[#0D9488]'
                  }`}
                  data-testid={`interest-${interest.toLowerCase().replace(' ', '-')}`}
                >
                  {interest}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Preferred Trades</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {allTrades.map(trade => (
                <button
                  key={trade}
                  onClick={() => toggleTrade(trade)}
                  className={`px-4 py-2 rounded-full border transition-all ${
                    profile.preferred_trades.includes(trade)
                      ? 'bg-[#0F172A] text-white border-[#0F172A]'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-[#0F172A]'
                  }`}
                  data-testid={`trade-${trade.toLowerCase().replace(' ', '-')}`}
                >
                  {trade}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="consented"
              checked={profile.consented}
              onChange={(e) => setProfile({ ...profile, consented: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300"
            />
            <Label htmlFor="consented" className="text-sm">
              I consent to sharing my information with potential employers
            </Label>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#0F172A] hover:bg-[#1E293B] rounded-full"
            data-testid="save-profile-btn"
          >
            <Save className="mr-2 w-4 h-4" />
            {saving ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>

        {profile.has_paid && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4 text-center text-green-800">
            ✓ Payment completed! You can now access assessments and CV builder.
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
